import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Edit, Trash2, Save, X, ArrowUp, ArrowDown, Settings, TrendingUp, BarChart3, FileText, Target, ArrowLeft, Eye, EyeOff, Archive } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Layout } from "@/components/Layout";
import PerformanceAttributionPanel from "@/components/risk/PerformanceAttributionPanel";

interface PortfolioRange {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  amc: string;
  risk_type: string;
  allocation: string;
  rebalancing: string;
  highlight_badge: string;
  slug: string;
  status: string;
  sort_order: number;
}

interface IndividualPortfolio {
  id: string;
  range_id: string;
  name: string;
  slug: string;
  risk_level: string;
  target_audience: string;
  description: string;
  expected_return: string;
  volatility: string;
  benchmark: string;
  key_characteristics: any;
  performance_characteristics: any;
  target_allocation: any;
  status: string;
  sort_order: number;
}

interface PortfolioFeature {
  id: string;
  range_id?: string;
  individual_portfolio_id?: string;
  feature_text: string;
  sort_order: number;
}

interface PortfolioContent {
  id: string;
  portfolio_code: string;
  title: string;
  subtitle?: string;
  description: string;
  key_characteristics: string[];
  expected_annual_return?: string;
  expected_volatility?: string;
  sectoral_allocation?: string;
  target_audience?: string;
  allocation_summary?: string;
  rebalancing_frequency?: string;
  risk_profile?: string;
  status: string;
}

interface PortfolioBenchmark {
  id: string;
  portfolio_code: string;
  benchmark_type: string;
  benchmark_name: string;
  benchmark_short_name?: string;
  display_order: number;
  show_excess_calculation: boolean;
  excess_label?: string;
}

const PortfolioManagement = () => {
  const [portfolioRanges, setPortfolioRanges] = useState<PortfolioRange[]>([]);
  const [individualPortfolios, setIndividualPortfolios] = useState<IndividualPortfolio[]>([]);
  const [features, setFeatures] = useState<PortfolioFeature[]>([]);
  const [portfolioContents, setPortfolioContents] = useState<PortfolioContent[]>([]);
  const [portfolioBenchmarks, setPortfolioBenchmarks] = useState<PortfolioBenchmark[]>([]);
  const [activeTab, setActiveTab] = useState("ranges");
  const [selectedRange, setSelectedRange] = useState<PortfolioRange | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isIndividualDialogOpen, setIsIndividualDialogOpen] = useState(false);
  const [isContentDialogOpen, setIsContentDialogOpen] = useState(false);
  const [isBenchmarkDialogOpen, setIsBenchmarkDialogOpen] = useState(false);
  const [editingRange, setEditingRange] = useState<PortfolioRange | null>(null);
  const [editingPortfolio, setEditingPortfolio] = useState<IndividualPortfolio | null>(null);
  const [editingContent, setEditingContent] = useState<PortfolioContent | null>(null);
  const [editingBenchmark, setEditingBenchmark] = useState<PortfolioBenchmark | null>(null);
  const [loading, setLoading] = useState(true);
  const [rangeFeatures, setRangeFeatures] = useState<string[]>([]);
  const [newFeature, setNewFeature] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch portfolio ranges
      const { data: rangesData, error: rangesError } = await supabase
        .from('portfolio_ranges')
        .select('*')
        .order('sort_order');

      if (rangesError) throw rangesError;
      setPortfolioRanges(rangesData || []);

      // Fetch individual portfolios
      const { data: portfoliosData, error: portfoliosError } = await supabase
        .from('individual_portfolios')
        .select('*')
        .order('sort_order');

      if (portfoliosError) throw portfoliosError;
      setIndividualPortfolios(portfoliosData || []);

      // Fetch features
      const { data: featuresData, error: featuresError } = await supabase
        .from('portfolio_features')
        .select('*')
        .order('sort_order');

      if (featuresError) throw featuresError;
      setFeatures(featuresData || []);

      // Fetch portfolio contents
      const { data: contentsData, error: contentsError } = await supabase
        .from('portfolio_content')
        .select('*')
        .order('portfolio_code');

      if (contentsError) throw contentsError;
      
      // Transform the data to match our interfaces
      const transformedContents: PortfolioContent[] = (contentsData || []).map(content => ({
        ...content,
        key_characteristics: Array.isArray(content.key_characteristics) 
          ? content.key_characteristics as string[]
          : []
      }));
      setPortfolioContents(transformedContents);

      // Fetch portfolio benchmarks
      const { data: benchmarksData, error: benchmarksError } = await supabase
        .from('portfolio_benchmarks')
        .select('*')
        .order('portfolio_code')
        .order('display_order');

      if (benchmarksError) throw benchmarksError;
      setPortfolioBenchmarks(benchmarksData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch portfolio data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRange = async (formData: FormData) => {
    try {
      const rangeData = {
        name: formData.get('name') as string,
        subtitle: formData.get('subtitle') as string,
        description: formData.get('description') as string,
        amc: formData.get('amc') as string,
        risk_type: formData.get('risk_type') as string,
        allocation: formData.get('allocation') as string,
        rebalancing: formData.get('rebalancing') as string,
        highlight_badge: formData.get('highlight_badge') as string,
        slug: (formData.get('name') as string).toLowerCase().replace(/\s+/g, '-'),
        sort_order: parseInt(formData.get('sort_order') as string) || 0,
      };

      if (editingRange) {
        const { error } = await supabase
          .from('portfolio_ranges')
          .update(rangeData)
          .eq('id', editingRange.id);
        
        if (error) throw error;

        // Save features for existing range
        await handleSaveRangeFeatures(editingRange.id);
        
        toast({
          title: "Success",
          description: "Portfolio range updated successfully",
        });
      } else {
        const { data: newRange, error } = await supabase
          .from('portfolio_ranges')
          .insert([rangeData])
          .select()
          .single();
        
        if (error) throw error;

        // Save features for new range
        if (newRange) {
          await handleSaveRangeFeatures(newRange.id);
        }
        
        toast({
          title: "Success", 
          description: "Portfolio range created successfully",
        });
      }

      setIsDialogOpen(false);
      setEditingRange(null);
      setRangeFeatures([]);
      setNewFeature('');
      fetchData();
    } catch (error) {
      console.error('Error saving range:', error);
      toast({
        title: "Error",
        description: "Failed to save portfolio range",
        variant: "destructive",
      });
    }
  };

  const handleSaveRangeFeatures = async (rangeId: string) => {
    try {
      // Delete existing features for this range
      await supabase
        .from('portfolio_features')
        .delete()
        .eq('range_id', rangeId);

      // Insert new features
      if (rangeFeatures.length > 0) {
        const featureData = rangeFeatures.map((featureText, index) => ({
          range_id: rangeId,
          feature_text: featureText,
          sort_order: index + 1
        }));

        const { error } = await supabase
          .from('portfolio_features')
          .insert(featureData);

        if (error) throw error;
      }
    } catch (error) {
      console.error('Error saving range features:', error);
      throw error;
    }
  };

  const addRangeFeature = () => {
    if (newFeature.trim() && !rangeFeatures.includes(newFeature.trim())) {
      setRangeFeatures([...rangeFeatures, newFeature.trim()]);
      setNewFeature('');
    }
  };

  const removeRangeFeature = (index: number) => {
    setRangeFeatures(rangeFeatures.filter((_, i) => i !== index));
  };

  const loadRangeFeatures = (rangeId: string) => {
    const existingFeatures = features
      .filter(f => f.range_id === rangeId)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map(f => f.feature_text);
    setRangeFeatures(existingFeatures);
  };

  const handleSaveIndividualPortfolio = async (formData: FormData) => {
    try {
      const portfolioData = {
        range_id: formData.get('range_id') as string,
        name: formData.get('name') as string,
        slug: (formData.get('name') as string).toLowerCase().replace(/\s+/g, '-'),
        risk_level: formData.get('risk_level') as string,
        target_audience: formData.get('target_audience') as string,
        description: formData.get('description') as string,
        expected_return: formData.get('expected_return') as string,
        volatility: formData.get('volatility') as string,
        benchmark: formData.get('benchmark') as string,
        key_characteristics: JSON.parse(formData.get('key_characteristics') as string || '[]'),
        performance_characteristics: JSON.parse(formData.get('performance_characteristics') as string || '{}'),
        target_allocation: JSON.parse(formData.get('target_allocation') as string || '{}'),
        status: formData.get('status') as string,
        sort_order: parseInt(formData.get('sort_order') as string) || 0,
      };

      if (editingPortfolio) {
        const { error } = await supabase
          .from('individual_portfolios')
          .update(portfolioData)
          .eq('id', editingPortfolio.id);
        
        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Portfolio updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('individual_portfolios')
          .insert([portfolioData]);
        
        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Portfolio created successfully",
        });
      }

      setIsIndividualDialogOpen(false);
      setEditingPortfolio(null);
      fetchData();
    } catch (error) {
      console.error('Error saving portfolio:', error);
      toast({
        title: "Error",
        description: "Failed to save portfolio",
        variant: "destructive",
      });
    }
  };

  const handleDeleteRange = async (id: string) => {
    if (!confirm('Are you sure you want to delete this portfolio range? This will also delete all associated portfolios.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('portfolio_ranges')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Portfolio range deleted successfully",
      });
      
      fetchData();
    } catch (error) {
      console.error('Error deleting range:', error);
      toast({
        title: "Error",
        description: "Failed to delete portfolio range",
        variant: "destructive",
      });
    }
  };

  const handleDeletePortfolio = async (id: string) => {
    if (!confirm('Are you sure you want to delete this portfolio?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('individual_portfolios')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Portfolio deleted successfully",
      });
      
      fetchData();
    } catch (error) {
      console.error('Error deleting portfolio:', error);
      toast({
        title: "Error",
        description: "Failed to delete portfolio",
        variant: "destructive",
      });
    }
  };

  const updateSortOrder = async (items: any[], tableName: 'portfolio_ranges' | 'individual_portfolios') => {
    try {
      const updates = items.map((item, index) => ({
        id: item.id,
        sort_order: index + 1
      }));

      for (const update of updates) {
        await supabase
          .from(tableName)
          .update({ sort_order: update.sort_order })
          .eq('id', update.id);
      }

      toast({
        title: "Success",
        description: "Order updated successfully",
      });
      
      fetchData();
    } catch (error) {
      console.error('Error updating order:', error);
      toast({
        title: "Error",
        description: "Failed to update order",
        variant: "destructive",
      });
    }
  };

  const handleSaveContent = async (formData: FormData) => {
    try {
      const contentData = {
        portfolio_code: formData.get('portfolio_code') as string,
        title: formData.get('title') as string,
        subtitle: formData.get('subtitle') as string || null,
        description: formData.get('description') as string,
        key_characteristics: JSON.parse(formData.get('key_characteristics') as string || '[]'),
        expected_annual_return: formData.get('expected_annual_return') as string || null,
        expected_volatility: formData.get('expected_volatility') as string || null,
        sectoral_allocation: formData.get('sectoral_allocation') as string || null,
        target_audience: formData.get('target_audience') as string || null,
        allocation_summary: formData.get('allocation_summary') as string || null,
        rebalancing_frequency: formData.get('rebalancing_frequency') as string || null,
        risk_profile: formData.get('risk_profile') as string || null,
        status: formData.get('status') as string || 'active',
      };

      if (editingContent) {
        const { error } = await supabase
          .from('portfolio_content')
          .update(contentData)
          .eq('id', editingContent.id);
        
        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Portfolio content updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('portfolio_content')
          .insert([contentData]);
        
        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Portfolio content created successfully",
        });
      }

      setIsContentDialogOpen(false);
      setEditingContent(null);
      fetchData();
    } catch (error) {
      console.error('Error saving content:', error);
      toast({
        title: "Error",
        description: "Failed to save portfolio content",
        variant: "destructive",
      });
    }
  };

  const handleSaveBenchmark = async (formData: FormData) => {
    try {
      const benchmarkData = {
        portfolio_code: formData.get('portfolio_code') as string,
        benchmark_type: formData.get('benchmark_type') as string,
        benchmark_name: formData.get('benchmark_name') as string,
        benchmark_short_name: formData.get('benchmark_short_name') as string || null,
        display_order: parseInt(formData.get('display_order') as string) || 1,
        show_excess_calculation: formData.get('show_excess_calculation') === 'on',
        excess_label: formData.get('excess_label') as string || null,
      };

      if (editingBenchmark) {
        const { error } = await supabase
          .from('portfolio_benchmarks')
          .update(benchmarkData)
          .eq('id', editingBenchmark.id);
        
        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Benchmark updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('portfolio_benchmarks')
          .insert([benchmarkData]);
        
        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Benchmark created successfully",
        });
      }

      setIsBenchmarkDialogOpen(false);
      setEditingBenchmark(null);
      fetchData();
    } catch (error) {
      console.error('Error saving benchmark:', error);
      toast({
        title: "Error",
        description: "Failed to save benchmark",
        variant: "destructive",
      });
    }
  };

  const handleDeleteContent = async (id: string) => {
    if (!confirm('Are you sure you want to delete this portfolio content?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('portfolio_content')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Portfolio content deleted successfully",
      });
      
      fetchData();
    } catch (error) {
      console.error('Error deleting content:', error);
      toast({
        title: "Error",
        description: "Failed to delete portfolio content",
        variant: "destructive",
      });
    }
  };

  const handleDeleteBenchmark = async (id: string) => {
    if (!confirm('Are you sure you want to delete this benchmark?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('portfolio_benchmarks')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Benchmark deleted successfully",
      });
      
      fetchData();
    } catch (error) {
      console.error('Error deleting benchmark:', error);
      toast({
        title: "Error",
        description: "Failed to delete benchmark",
        variant: "destructive",
      });
    }
  };

  const handlePortfolioStatusChange = async (portfolioId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('individual_portfolios')
        .update({ status: newStatus })
        .eq('id', portfolioId);
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: `Portfolio ${newStatus} successfully`,
      });
      
      fetchData();
    } catch (error) {
      console.error('Error updating portfolio status:', error);
      toast({
        title: "Error",
        description: "Failed to update portfolio status",
        variant: "destructive",
      });
    }
  };

  const moveItem = (items: any[], index: number, direction: 'up' | 'down') => {
    const newItems = [...items];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < newItems.length) {
      [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
      return newItems;
    }
    return items;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading portfolio data...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Portfolio Management</h1>
            <p className="text-muted-foreground">Manage portfolio ranges, individual portfolios, and their features</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-sm">
              <BarChart3 className="h-4 w-4 mr-1" />
              {portfolioRanges.length} Ranges
            </Badge>
            <Badge variant="outline" className="text-sm">
              <TrendingUp className="h-4 w-4 mr-1" />
              {individualPortfolios.length} Portfolios
            </Badge>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="ranges">Portfolio Ranges</TabsTrigger>
            <TabsTrigger value="individual">Individual Portfolios</TabsTrigger>
            <TabsTrigger value="content">Portfolio Content</TabsTrigger>
            <TabsTrigger value="attribution-p">P Attribution</TabsTrigger>
            <TabsTrigger value="attribution-r">R Attribution</TabsTrigger>
          </TabsList>

          <TabsContent value="ranges" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">Portfolio Ranges</h2>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => {
                  setEditingRange(null);
                  setRangeFeatures([]);
                  setNewFeature('');
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Range
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingRange ? 'Edit Portfolio Range' : 'Add New Portfolio Range'}
                  </DialogTitle>
                  <DialogDescription>
                    {editingRange ? 'Update the portfolio range details below.' : 'Create a new portfolio range with the details below.'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  handleSaveRange(formData);
                }} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Name</Label>
                      <Input 
                        id="name" 
                        name="name" 
                        defaultValue={editingRange?.name || ''} 
                        required 
                      />
                    </div>
                    <div>
                      <Label htmlFor="subtitle">Subtitle</Label>
                      <Input 
                        id="subtitle" 
                        name="subtitle" 
                        defaultValue={editingRange?.subtitle || ''} 
                        required 
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea 
                      id="description" 
                      name="description" 
                      defaultValue={editingRange?.description || ''} 
                      required 
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="amc">Annual Management Charge</Label>
                      <Input 
                        id="amc" 
                        name="amc" 
                        placeholder="e.g. 0.35%" 
                        defaultValue={editingRange?.amc || ''} 
                        required 
                      />
                    </div>
                    <div>
                      <Label htmlFor="risk_type">Management Approach</Label>
                      <Select name="risk_type" defaultValue={editingRange?.risk_type || ''}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select management approach" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Active">Active</SelectItem>
                          <SelectItem value="Passive">Passive</SelectItem>
                          <SelectItem value="Passive Preferred">Passive Preferred</SelectItem>
                          <SelectItem value="Income-Focused">Income-Focused</SelectItem>
                          <SelectItem value="Sustainable">Sustainable</SelectItem>
                          <SelectItem value="Balanced">Balanced</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="allocation">Allocation Strategy</Label>
                      <Input 
                        id="allocation" 
                        name="allocation" 
                        placeholder="e.g. Full Investment Universe" 
                        defaultValue={editingRange?.allocation || ''} 
                        required 
                      />
                    </div>
                    <div>
                      <Label htmlFor="rebalancing">Rebalancing Frequency</Label>
                      <Select name="rebalancing" defaultValue={editingRange?.rebalancing || ''}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Monthly">Monthly</SelectItem>
                          <SelectItem value="Quarterly">Quarterly</SelectItem>
                          <SelectItem value="Semi-Annually">Semi-Annually</SelectItem>
                          <SelectItem value="Annually">Annually</SelectItem>
                          <SelectItem value="As Required">As Required</SelectItem>
                          <SelectItem value="TBC">TBC</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="highlight_badge">Highlight Badge</Label>
                      <Select name="highlight_badge" defaultValue={editingRange?.highlight_badge || ''}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select badge type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Premium">Premium</SelectItem>
                          <SelectItem value="Value">Value</SelectItem>
                          <SelectItem value="Income">Income</SelectItem>
                          <SelectItem value="ESG">ESG</SelectItem>
                          <SelectItem value="New">New</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="sort_order">Sort Order</Label>
                      <Input 
                        id="sort_order" 
                        name="sort_order" 
                        type="number" 
                        defaultValue={editingRange?.sort_order || 0} 
                        min="0" 
                      />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Label>Portfolio Features</Label>
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <Input
                              placeholder="Add new feature..."
                              value={newFeature}
                              onChange={(e) => setNewFeature(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  addRangeFeature();
                                }
                              }}
                            />
                            <Button 
                              type="button" 
                              onClick={addRangeFeature}
                              disabled={!newFeature.trim()}
                            >
                              Add
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-2 min-h-[40px] p-2 border rounded-md">
                            {rangeFeatures.length === 0 ? (
                              <p className="text-muted-foreground text-sm">No features added yet</p>
                            ) : (
                              rangeFeatures.map((feature, index) => (
                                <Badge
                                  key={index}
                                  variant="secondary"
                                  className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                                  onClick={() => removeRangeFeature(index)}
                                  title="Click to remove"
                                >
                                  {feature} ✕
                                </Badge>
                              ))
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Click on a feature to remove it
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button type="submit">
                      <Save className="h-4 w-4 mr-2" />
                      {editingRange ? 'Update' : 'Create'} Range
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-6">
            {portfolioRanges.map((range, index) => (
              <Card key={range.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          {range.name}
                          <Badge variant={
                            range.highlight_badge === "Premium" ? "default" : 
                            range.highlight_badge === "Value" ? "secondary" : 
                            range.highlight_badge === "Income" ? "destructive" : "outline"
                          }>
                            {range.highlight_badge}
                          </Badge>
                        </CardTitle>
                        <CardDescription>{range.subtitle}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const reordered = moveItem(portfolioRanges, index, 'up');
                          updateSortOrder(reordered, 'portfolio_ranges');
                        }}
                        disabled={index === 0}
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const reordered = moveItem(portfolioRanges, index, 'down');
                          updateSortOrder(reordered, 'portfolio_ranges');
                        }}
                        disabled={index === portfolioRanges.length - 1}
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingRange(range);
                            loadRangeFeatures(range.id);
                            setIsDialogOpen(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteRange(range.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-sm font-medium">AMC</p>
                      <p className="text-lg font-bold text-primary">{range.amc}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Management Approach</p>
                      <p className="text-sm text-muted-foreground">{range.risk_type}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Allocation</p>
                      <p className="text-sm text-muted-foreground">{range.allocation}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Rebalancing</p>
                      <p className="text-sm text-muted-foreground">{range.rebalancing}</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">{range.description}</p>
                  
                  <div className="mb-4">
                    <p className="text-sm font-medium mb-2">Features:</p>
                    <div className="flex flex-wrap gap-2">
                      {features
                        .filter(f => f.range_id === range.id)
                        .map(feature => (
                          <Badge key={feature.id} variant="outline" className="text-xs">
                            {feature.feature_text}
                          </Badge>
                        ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium mb-2">
                      Individual Portfolios ({individualPortfolios.filter(p => p.range_id === range.id).length}):
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {individualPortfolios
                        .filter(p => p.range_id === range.id)
                        .sort((a, b) => a.sort_order - b.sort_order)
                        .map(portfolio => (
                          <Badge key={portfolio.id} variant="secondary" className="text-xs">
                            {portfolio.name}
                          </Badge>
                        ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="individual" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">Individual Portfolios</h2>
            <Dialog open={isIndividualDialogOpen} onOpenChange={setIsIndividualDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setEditingPortfolio(null)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Portfolio
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingPortfolio ? 'Edit Individual Portfolio' : 'Add New Individual Portfolio'}
                  </DialogTitle>
                  <DialogDescription>
                    {editingPortfolio ? 'Update the individual portfolio details below.' : 'Create a new individual portfolio with the details below.'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  handleSaveIndividualPortfolio(formData);
                }} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="range_id">Portfolio Range</Label>
                      <Select name="range_id" defaultValue={editingPortfolio?.range_id || ''}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select portfolio range" />
                        </SelectTrigger>
                        <SelectContent>
                          {portfolioRanges.map(range => (
                            <SelectItem key={range.id} value={range.id}>
                              {range.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="name">Portfolio Name</Label>
                      <Input 
                        id="name" 
                        name="name" 
                        defaultValue={editingPortfolio?.name || ''} 
                        required 
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="risk_level">Risk Level</Label>
                      <Input 
                        id="risk_level" 
                        name="risk_level" 
                        placeholder="e.g. Defensive, Balanced, Aggressive" 
                        defaultValue={editingPortfolio?.risk_level || ''} 
                        required 
                      />
                    </div>
                    <div>
                      <Label htmlFor="target_audience">Target Audience</Label>
                      <Input 
                        id="target_audience" 
                        name="target_audience" 
                        placeholder="e.g. Conservative investors" 
                        defaultValue={editingPortfolio?.target_audience || ''} 
                        required 
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea 
                      id="description" 
                      name="description" 
                      defaultValue={editingPortfolio?.description || ''} 
                      required 
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="expected_return">Expected Return</Label>
                      <Input 
                        id="expected_return" 
                        name="expected_return" 
                        placeholder="e.g. 5-7%" 
                        defaultValue={editingPortfolio?.expected_return || ''} 
                      />
                    </div>
                    <div>
                      <Label htmlFor="volatility">Volatility</Label>
                      <Input 
                        id="volatility" 
                        name="volatility" 
                        placeholder="e.g. 4-8%" 
                        defaultValue={editingPortfolio?.volatility || ''} 
                      />
                    </div>
                    <div>
                      <Label htmlFor="benchmark">Benchmark</Label>
                      <Input 
                        id="benchmark" 
                        name="benchmark" 
                        placeholder="e.g. CBAM I Benchmark" 
                        defaultValue={editingPortfolio?.benchmark || ''} 
                        required 
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="key_characteristics">Key Characteristics (JSON Array)</Label>
                    <Textarea 
                      id="key_characteristics" 
                      name="key_characteristics" 
                      placeholder='["Capital preservation focus", "Low volatility profile", "High-grade bond allocation"]'
                      defaultValue={JSON.stringify(editingPortfolio?.key_characteristics || [], null, 2)} 
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label htmlFor="performance_characteristics">Performance Characteristics (JSON Object)</Label>
                    <Textarea 
                      id="performance_characteristics" 
                      name="performance_characteristics" 
                      placeholder='{"annual_return": "5-7%", "volatility": "4-8%", "target_audience": "Balanced"}'
                      defaultValue={JSON.stringify(editingPortfolio?.performance_characteristics || {}, null, 2)} 
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label htmlFor="target_allocation">Target Allocation (JSON Object)</Label>
                    <Textarea 
                      id="target_allocation" 
                      name="target_allocation" 
                      placeholder='{"fixed_income": 60, "equity": 40}'
                      defaultValue={JSON.stringify(editingPortfolio?.target_allocation || {}, null, 2)} 
                      rows={3}
                    />
                   </div>

                   <div className="grid grid-cols-2 gap-4">
                     <div>
                       <Label htmlFor="status">Status</Label>
                       <Select name="status" defaultValue={editingPortfolio?.status || 'active'}>
                         <SelectTrigger>
                           <SelectValue placeholder="Select status" />
                         </SelectTrigger>
                         <SelectContent>
                           <SelectItem value="active">Published</SelectItem>
                           <SelectItem value="hidden">Hidden</SelectItem>
                           <SelectItem value="archived">Archived</SelectItem>
                         </SelectContent>
                       </Select>
                     </div>
                     <div>
                       <Label htmlFor="sort_order">Sort Order</Label>
                       <Input 
                         id="sort_order" 
                         name="sort_order" 
                         type="number" 
                         defaultValue={editingPortfolio?.sort_order || 0} 
                         min="0" 
                       />
                     </div>
                   </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsIndividualDialogOpen(false)}>
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button type="submit">
                      <Save className="h-4 w-4 mr-2" />
                      {editingPortfolio ? 'Update' : 'Create'} Portfolio
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4">
            {individualPortfolios
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((portfolio, index) => {
                const range = portfolioRanges.find(r => r.id === portfolio.range_id);
                return (
                  <Card key={portfolio.id}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            {portfolio.name}
                            <Badge variant="outline">{range?.name}</Badge>
                            <Badge variant="secondary">{portfolio.risk_level}</Badge>
                          </CardTitle>
                          <CardDescription>{portfolio.target_audience}</CardDescription>
                        </div>
                         <div className="flex items-center gap-2">
                           {portfolio.status === 'active' && (
                             <Button
                               variant="outline"
                               size="sm"
                               onClick={() => handlePortfolioStatusChange(portfolio.id, 'hidden')}
                               title="Hide Portfolio"
                             >
                               <EyeOff className="h-4 w-4" />
                             </Button>
                           )}
                           {portfolio.status === 'hidden' && (
                             <Button
                               variant="outline"
                               size="sm"
                               onClick={() => handlePortfolioStatusChange(portfolio.id, 'active')}
                               title="Publish Portfolio"
                             >
                               <Eye className="h-4 w-4" />
                             </Button>
                           )}
                           {portfolio.status !== 'archived' && (
                             <Button
                               variant="outline"
                               size="sm"
                               onClick={() => handlePortfolioStatusChange(portfolio.id, 'archived')}
                               title="Archive Portfolio"
                             >
                               <Archive className="h-4 w-4" />
                             </Button>
                           )}
                           <Button
                             variant="outline"
                             size="sm"
                             onClick={() => {
                               const reordered = moveItem(individualPortfolios, index, 'up');
                               updateSortOrder(reordered, 'individual_portfolios');
                             }}
                             disabled={index === 0}
                           >
                             <ArrowUp className="h-4 w-4" />
                           </Button>
                           <Button
                             variant="outline"
                             size="sm"
                             onClick={() => {
                               const reordered = moveItem(individualPortfolios, index, 'down');
                               updateSortOrder(reordered, 'individual_portfolios');
                             }}
                             disabled={index === individualPortfolios.length - 1}
                           >
                             <ArrowDown className="h-4 w-4" />
                           </Button>
                           <Button
                             variant="outline"
                             size="sm"
                             onClick={() => {
                               setEditingPortfolio(portfolio);
                               setIsIndividualDialogOpen(true);
                             }}
                           >
                             <Edit className="h-4 w-4" />
                           </Button>
                           <Button
                             variant="outline"
                             size="sm"
                             onClick={() => handleDeletePortfolio(portfolio.id)}
                           >
                             <Trash2 className="h-4 w-4" />
                           </Button>
                         </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-sm font-medium">Expected Return</p>
                          <p className="text-sm font-bold text-primary">{portfolio.expected_return || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Volatility</p>
                          <p className="text-sm text-muted-foreground">{portfolio.volatility || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Benchmark</p>
                          <p className="text-sm text-muted-foreground">{portfolio.benchmark}</p>
                        </div>
                         <div>
                           <p className="text-sm font-medium">Status</p>
                           <Badge variant={
                             portfolio.status === 'active' ? 'default' : 
                             portfolio.status === 'hidden' ? 'secondary' : 
                             'outline'
                           }>
                             {portfolio.status === 'active' ? 'Published' : 
                              portfolio.status === 'hidden' ? 'Hidden' : 
                              'Archived'}
                           </Badge>
                         </div>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-4">{portfolio.description}</p>
                      
                      {portfolio.key_characteristics && Array.isArray(portfolio.key_characteristics) && portfolio.key_characteristics.length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-medium mb-2">Key Characteristics:</p>
                          <div className="flex flex-wrap gap-2">
                            {portfolio.key_characteristics.map((char: string, idx: number) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {char}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
          </div>
        </TabsContent>

        {/* Portfolio Content Tab */}
        <TabsContent value="content" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-semibold">Portfolio Content Management</h2>
              <p className="text-muted-foreground">Manage portfolio content, benchmarks, and display settings</p>
            </div>
          </div>

          <div className="grid gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-6">
                <div className="flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-primary" />
                  <div>
                    <CardTitle>Portfolio Content</CardTitle>
                    <CardDescription>
                      Manage portfolio headers, descriptions, and characteristics that appear on portfolio detail pages
                    </CardDescription>
                  </div>
                </div>
                <Dialog open={isContentDialogOpen} onOpenChange={setIsContentDialogOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={() => setEditingContent(null)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Content
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {editingContent ? 'Edit Portfolio Content' : 'Add New Portfolio Content'}
                      </DialogTitle>
                      <DialogDescription>
                        {editingContent ? 'Update the portfolio content details below.' : 'Create new portfolio content with the details below.'}
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.currentTarget);
                      handleSaveContent(formData);
                    }} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="portfolio_code">Portfolio Code</Label>
                          <Input 
                            id="portfolio_code" 
                            name="portfolio_code" 
                            placeholder="e.g. R1, P1, DIST" 
                            defaultValue={editingContent?.portfolio_code || ''} 
                            required 
                          />
                        </div>
                        <div>
                          <Label htmlFor="status">Status</Label>
                          <Select name="status" defaultValue={editingContent?.status || 'active'}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="inactive">Inactive</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="title">Title</Label>
                          <Input 
                            id="title" 
                            name="title" 
                            defaultValue={editingContent?.title || ''} 
                            required 
                          />
                        </div>
                        <div>
                          <Label htmlFor="subtitle">Subtitle</Label>
                          <Input 
                            id="subtitle" 
                            name="subtitle" 
                            defaultValue={editingContent?.subtitle || ''} 
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="description">Description</Label>
                        <Textarea 
                          id="description" 
                          name="description" 
                          defaultValue={editingContent?.description || ''} 
                          required 
                          rows={3}
                        />
                      </div>

                      <div>
                        <Label htmlFor="key_characteristics">Key Characteristics (JSON Array)</Label>
                        <Textarea 
                          id="key_characteristics" 
                          name="key_characteristics" 
                          placeholder='["Capital preservation focus", "Low volatility profile", "High-grade bond allocation"]'
                          defaultValue={JSON.stringify(editingContent?.key_characteristics || [], null, 2)} 
                          rows={3}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="expected_annual_return">Expected Annual Return</Label>
                          <Input 
                            id="expected_annual_return" 
                            name="expected_annual_return" 
                            placeholder="e.g. 5-7%" 
                            defaultValue={editingContent?.expected_annual_return || ''} 
                          />
                        </div>
                        <div>
                          <Label htmlFor="expected_volatility">Expected Volatility</Label>
                          <Input 
                            id="expected_volatility" 
                            name="expected_volatility" 
                            placeholder="e.g. 4-8%" 
                            defaultValue={editingContent?.expected_volatility || ''} 
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="target_audience">Target Audience</Label>
                          <Input 
                            id="target_audience" 
                            name="target_audience" 
                            placeholder="e.g. Conservative investors" 
                            defaultValue={editingContent?.target_audience || ''} 
                          />
                        </div>
                        <div>
                          <Label htmlFor="risk_profile">Risk Profile</Label>
                          <Input 
                            id="risk_profile" 
                            name="risk_profile" 
                            placeholder="e.g. Low to Medium" 
                            defaultValue={editingContent?.risk_profile || ''} 
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="sectoral_allocation">Sectoral Allocation</Label>
                          <Input 
                            id="sectoral_allocation" 
                            name="sectoral_allocation" 
                            placeholder="e.g. Diversified across sectors" 
                            defaultValue={editingContent?.sectoral_allocation || ''} 
                          />
                        </div>
                        <div>
                          <Label htmlFor="rebalancing_frequency">Rebalancing Frequency</Label>
                          <Input 
                            id="rebalancing_frequency" 
                            name="rebalancing_frequency" 
                            placeholder="e.g. Quarterly" 
                            defaultValue={editingContent?.rebalancing_frequency || ''} 
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="allocation_summary">Allocation Summary</Label>
                        <Textarea 
                          id="allocation_summary" 
                          name="allocation_summary" 
                          placeholder="Brief summary of asset allocation strategy"
                          defaultValue={editingContent?.allocation_summary || ''} 
                          rows={2}
                        />
                      </div>

                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setIsContentDialogOpen(false)}>
                          <X className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                        <Button type="submit">
                          <Save className="h-4 w-4 mr-2" />
                          {editingContent ? 'Update' : 'Create'} Content
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {portfolioContents.map((content) => (
                    <Card key={content.id} className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold">{content.portfolio_code}</h4>
                            <Badge variant={content.status === 'active' ? 'default' : 'secondary'}>
                              {content.status}
                            </Badge>
                          </div>
                          <h5 className="font-medium">{content.title}</h5>
                          {content.subtitle && <p className="text-sm text-muted-foreground">{content.subtitle}</p>}
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{content.description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingContent(content);
                              setIsContentDialogOpen(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteContent(content.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                  {portfolioContents.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No portfolio content found</p>
                      <p className="text-sm">Create portfolio content to manage page headers and descriptions</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-6">
                <div className="flex items-center space-x-2">
                  <Target className="h-5 w-5 text-primary" />
                  <div>
                    <CardTitle>Performance Benchmarks</CardTitle>
                    <CardDescription>
                      Configure which benchmarks each portfolio is measured against in performance summaries
                    </CardDescription>
                  </div>
                </div>
                <Dialog open={isBenchmarkDialogOpen} onOpenChange={setIsBenchmarkDialogOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={() => setEditingBenchmark(null)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Benchmark
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {editingBenchmark ? 'Edit Portfolio Benchmark' : 'Add New Portfolio Benchmark'}
                      </DialogTitle>
                      <DialogDescription>
                        {editingBenchmark ? 'Update the benchmark details below.' : 'Create a new benchmark configuration with the details below.'}
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.currentTarget);
                      handleSaveBenchmark(formData);
                    }} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="portfolio_code">Portfolio Code</Label>
                          <Input 
                            id="portfolio_code" 
                            name="portfolio_code" 
                            placeholder="e.g. R1, P1, DIST" 
                            defaultValue={editingBenchmark?.portfolio_code || ''} 
                            required 
                          />
                        </div>
                        <div>
                          <Label htmlFor="benchmark_type">Benchmark Type</Label>
                          <Select name="benchmark_type" defaultValue={editingBenchmark?.benchmark_type || ''}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select benchmark type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="primary">Primary</SelectItem>
                              <SelectItem value="secondary">Secondary</SelectItem>
                              <SelectItem value="composite">Composite</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="benchmark_name">Benchmark Name</Label>
                          <Input 
                            id="benchmark_name" 
                            name="benchmark_name" 
                            placeholder="e.g. CBAM I Benchmark" 
                            defaultValue={editingBenchmark?.benchmark_name || ''} 
                            required 
                          />
                        </div>
                        <div>
                          <Label htmlFor="benchmark_short_name">Short Name (Optional)</Label>
                          <Input 
                            id="benchmark_short_name" 
                            name="benchmark_short_name" 
                            placeholder="e.g. CBAM I" 
                            defaultValue={editingBenchmark?.benchmark_short_name || ''} 
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="display_order">Display Order</Label>
                          <Input 
                            id="display_order" 
                            name="display_order" 
                            type="number" 
                            defaultValue={editingBenchmark?.display_order || 1} 
                            min="1" 
                            required 
                          />
                        </div>
                        <div>
                          <Label htmlFor="excess_label">Excess Return Label (Optional)</Label>
                          <Input 
                            id="excess_label" 
                            name="excess_label" 
                            placeholder="e.g. vs CBAM I" 
                            defaultValue={editingBenchmark?.excess_label || ''} 
                          />
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="show_excess_calculation"
                          name="show_excess_calculation"
                          defaultChecked={editingBenchmark?.show_excess_calculation ?? true}
                        />
                        <Label htmlFor="show_excess_calculation" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          Show excess return calculation
                        </Label>
                      </div>

                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setIsBenchmarkDialogOpen(false)}>
                          <X className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                        <Button type="submit">
                          <Save className="h-4 w-4 mr-2" />
                          {editingBenchmark ? 'Update' : 'Create'} Benchmark
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {portfolioBenchmarks
                    .sort((a, b) => a.portfolio_code.localeCompare(b.portfolio_code) || a.display_order - b.display_order)
                    .map((benchmark) => (
                    <Card key={benchmark.id} className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold">{benchmark.portfolio_code}</h4>
                            <Badge variant="outline">{benchmark.benchmark_type}</Badge>
                            <Badge variant="secondary">Order: {benchmark.display_order}</Badge>
                          </div>
                          <h5 className="font-medium">{benchmark.benchmark_name}</h5>
                          {benchmark.benchmark_short_name && (
                            <p className="text-sm text-muted-foreground">Short: {benchmark.benchmark_short_name}</p>
                          )}
                          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                            <span>Excess Calculation: {benchmark.show_excess_calculation ? 'Yes' : 'No'}</span>
                            {benchmark.excess_label && <span>Label: {benchmark.excess_label}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingBenchmark(benchmark);
                              setIsBenchmarkDialogOpen(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteBenchmark(benchmark.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                  {portfolioBenchmarks.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No benchmarks configured</p>
                      <p className="text-sm">Create benchmark configurations to define performance comparisons</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="attribution-p" className="space-y-6">
          <PerformanceAttributionPanel range="P" />
        </TabsContent>

        <TabsContent value="attribution-r" className="space-y-6">
          <PerformanceAttributionPanel range="R" />
        </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default PortfolioManagement;